/**
 * Helpers for a folder view rooted at one folder instead of the project root
 * (the Brief page over the project's Brief folder). Pure, so the rooting rules
 * are pinned without mounting the whole folder view.
 */

/** The flat list `useCustomFolders` returns (`DocumentFolder` rows). */
interface FlatFolder {
  id: string | number;
  parent_id: string | number | null;
}

interface NestedFolder {
  id: string | number;
  children?: NestedFolder[];
}

/**
 * The root and every folder under it, from the flat folder list. Null until
 * the list holds the root itself — an unloaded or failed list must not judge
 * any folder to be outside the root.
 */
function indexFolderChildren(folders: FlatFolder[]): Map<string, string[]> {
  const childrenOf = new Map<string, string[]>();
  for (const folder of folders) {
    if (folder.parent_id == null) continue;
    const parent = String(folder.parent_id);
    childrenOf.set(parent, [...(childrenOf.get(parent) ?? []), String(folder.id)]);
  }
  return childrenOf;
}

export function folderSubtreeIds(folders: FlatFolder[], rootId: string): Set<string> | null {
  if (!folders.some((folder) => String(folder.id) === rootId)) return null;
  const childrenOf = indexFolderChildren(folders);
  const ids = new Set<string>();
  const queue = [rootId];
  while (queue.length > 0) {
    const id = queue.pop() as string;
    if (ids.has(id)) continue;
    ids.add(id);
    queue.push(...(childrenOf.get(id) ?? []));
  }
  return ids;
}

/** A folder anywhere in a nested tree, by id. */
export function findTreeFolder<T extends NestedFolder>(folders: T[], id: string): T | null {
  for (const folder of folders) {
    if (String(folder.id) === id) return folder;
    const found = folder.children?.length ? findTreeFolder(folder.children as T[], id) : null;
    if (found) return found;
  }
  return null;
}
